import { Link } from 'react-router-dom';
import { TFunction } from 'i18next';
import {
  filterApplyTemplate,
  filterClearTemplate,
  inputTextTemplate,
} from '@/components/Table/Table';
import {
  Column,
  ColumnFilterElementTemplateOptions,
  ColumnProps,
} from 'primereact/column';
import { useMutation } from '@tanstack/react-query';
import { useState, useCallback } from 'react';

import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { faPen, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDateAndTime } from '@/utils/formatDate';
import { PagesPaths } from '@/enums';
import { Proforma } from '@/types/mySettlementType';
import { TableDropdownFilter } from '@/components';
import { TableData } from '@/components/Table/types';
import { getSettlementPdf } from '@/services/getSettlementPdf';
import { useDownloadPdf } from '@/helpers/downloadPdf';
import ModalDetails from './Components/ModalDetails';
import DeliveryOrderDownloadButton from './DeliveryOrderDownloadButton';
import styles from './MySettlement.module.scss';

type StatusColors = {
  [key: string]: string;
};

const STATUS_COLORS: StatusColors = {
  'En Proceso': '#374f93',
  'En Edición': '#ffd389',
  'Pendiente de Pago': '#fd8800',
  Agendada: '#4780bf',
  Paga: '#619b77',
  Solicitada: '#91c4fc',
  Validada: '#cbfcde',
  Rechazada: '#e24b39',
  Autorizada: '#7dd19d',
};

const PdfDownloadButton = ({ rowData }: { rowData: Proforma }) => {
  const { downloadPdf } = useDownloadPdf();
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  const {
    isPending: impresionBase64Loading,
    mutate: guardarPdfImpresionMutate,
  } = useMutation({
    mutationKey: ['guardarViajeSuscripcion'],
    mutationFn: getSettlementPdf,
    onSuccess: (data) => {
      downloadPdf(data, `Liquidacion_${rowData.oid}`);
    },
  });

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (debounceTimeout) clearTimeout(debounceTimeout);

      const newTimeout = setTimeout(() => {
        guardarPdfImpresionMutate({ oidLiquidacion: rowData.oid });
      }, 300);

      setDebounceTimeout(newTimeout);
    },
    [debounceTimeout, guardarPdfImpresionMutate, rowData.oid]
  );

  return (
    <button
      type="button"
      className={`flex justify-center ${styles.resetButton}`}
      aria-label="Download PDF"
      onClick={handleClick}
      disabled={impresionBase64Loading}
    >
      <FontAwesomeIcon icon={faFilePdf} style={{ cursor: 'pointer' }} />
    </button>
  );
};

export const getTableColumns = (
  translates: TFunction<'Documents', undefined>
): ColumnProps[] => [
  {
    field: 'checkbox',
    selectionMode: 'multiple',
  },
  {
    field: 'oid',
    header: translates('settlementNumber'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'tipo',
    header: translates('type'),
    body: (rowData: Proforma) => {
      if (rowData.tipo === 'VacíoImportación') {
        return 'Vacío Importación';
      }
      return rowData.tipo;
    },
    sortable: true,
  },
  {
    field: 'observaciones',
    header: translates('reference'),
    sortable: true,
  },
  {
    field: 'fechaInicio',
    header: translates('startDate'),
    body: (rowData: Proforma) => formatDateAndTime(rowData.fechaInicio),
    sortable: true,
  },
  {
    field: 'cliente',
    sortable: true,
    header: translates('client'),
  },
  {
    field: 'rutCliente',
    sortable: true,
    header: translates('clientRut'),
  },
  {
    field: 'estado',
    header: translates('status'),
    body: (rowData: Proforma) => {
      const backgroundColor = STATUS_COLORS[rowData.estado];
      return (
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          <div
            style={{
              backgroundColor,
              width: '10px',
              height: '10px',
              borderRadius: '100%',
            }}
          />
          <span>{rowData.estado}</span>
        </div>
      );
    },
  },
  {
    field: 'edit',
    body: (rowData: Proforma) => {
      const editStatuses = ['Agendada', 'En Edición'];
      if (!editStatuses.includes(rowData?.estado)) return null;

      let basePath: string;

      switch (rowData.tipo) {
        case 'Proforma':
          basePath = PagesPaths.ServicioAdicional;
          break;
        case 'DEPO20':
          basePath = PagesPaths.Depo20;
          break;
        case 'Importación':
          basePath = PagesPaths.ImportFCL;
          break;
        case 'Exportación':
          basePath = PagesPaths.ExportFCL;
          break;
        case 'VacíoImportación':
          basePath = PagesPaths.VacioImportacion;
          break;
        default:
          return null;
      }

      const isDisabled = rowData.estado === 'Agendada';
      const disableParam = isDisabled ? '&disabled=true' : '';

      return (
        <div className="flex justify-center">
          <Link to={`${basePath}?id=${rowData?.oid}${disableParam}`}>
            <FontAwesomeIcon icon={faPen} color="#223466" />
          </Link>
        </div>
      );
    },
  },
  {
    field: 'deliveryOrder',
    body: (rowData) => <DeliveryOrderDownloadButton rowData={rowData} />,
  },
  {
    field: 'view',
    body: (rowData) => (
      <div className="flex justify-center">
        <ModalDetails oid={rowData.oid} isExport={rowData.tipo} />
      </div>
    ),
  },
  {
    field: 'pdf',
    body: (rowData) => <PdfDownloadButton rowData={rowData} />,
  },
];

export const getHeaderColumnsGroup = (
  data: Proforma[] | undefined,
  translates: TFunction<'Documents', undefined>
) => (
  <ColumnGroup>
    <Row>
      <Column style={{ width: 40 }} />
      <Column
        field="oid"
        header={translates('settlementNumber')}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
      <Column
        field="tipo"
        header={translates('type')}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
      <Column
        field="observaciones"
        header={translates('reference')}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
      <Column
        field="fechaInicio"
        header={translates('startDate')}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
      <Column
        field="cliente"
        header={translates('client')}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
      <Column
        field="rutCliente"
        header={translates('clientRut')}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
      <Column
        field="estado"
        header={translates('status')}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <TableDropdownFilter
            options={options}
            data={data as unknown as TableData}
            fieldName="estado"
          />
        )}
      />
      <Column field="actions" style={{ width: 50 }} />
      <Column field="view" style={{ width: 50 }} />
      <Column field="pdf" style={{ width: 50 }} />
      <Column field="deliveryOrder" style={{ width: 50 }} />
    </Row>
  </ColumnGroup>
);
